import { Agency, AgencyManager, AgencyWithPagination } from 'core';
import { AgencyStatusFilter } from 'core/agency/Agency';
import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import { SearchBarModel } from 'components/common/SearchBar';
import { debounce } from 'lodash';
import { Pagination } from 'core/pagination/Pagination';
import {
  AgencyListColumns,
  AgencyListModel,
  DefaultAgencyListModel,
  RestaurantListModel
} from 'components/AgencyList';
import i18n from 'i18n';
import {
  ChannelGroupManager,
  DefaultChannelGroupManager
} from 'core/channelGroup/ChannelGroupManager';
import { FilterMenuTabConfig } from 'components/common/FilterMenuTab/FilterMenuTab';

export type AgenciesHomeState = {
  readonly loading: boolean;
  readonly error: Error | null;
  readonly agencies: Array<Agency>;
  readonly pagination: Pagination;
  readonly sortField: string;
  readonly sortOrder: 'asc' | 'desc';
  readonly searchString: string;
  readonly statusFilter: AgencyStatusFilter;
};

export interface AgenciesHomeModel extends SearchBarModel {
  readonly state: AgenciesHomeState;
  readonly event: UpdateEventListener<AgenciesHomeModel>;
  readonly title: string;
  readonly listModel: AgencyListModel;
  readonly filterMenuTabConfigs: Array<FilterMenuTabConfig>;

  fetch(): Promise<void>;
  onAgencyListChange(type: string, props: any): void;
  changeStatusFilter(status: AgencyStatusFilter): void;
  handleOnStatusFilterApply(values: string[]): void;
}

export type AgenciesHomeProps = {
  readonly model: AgenciesHomeModel;
};

export class DefaultAgenciesHomeModel implements AgenciesHomeModel {
  loading: boolean;
  error: Error | null;
  agencies: Array<Agency>;
  pagination: Pagination;
  sortField: string;
  sortOrder: 'asc' | 'desc';
  searchString: string;
  statusFilter: AgencyStatusFilter;
  debouncedFetch: any;

  agencyManager: AgencyManager;
  event: FireableUpdateEventListener<AgenciesHomeModel>;

  constructor(agencyManager: AgencyManager) {
    this.loading = false;
    this.error = null;
    this.agencies = [];
    this.pagination = {
      page: 1,
      size: 10,
      totalCount: 0
    };
    this.sortField = AgencyListColumns.AGENCY_ID;
    this.sortOrder = 'asc';
    this.searchString = '';
    this.statusFilter = AgencyStatusFilter.ACTIVE;
    this.debouncedFetch = debounce(this.fetch.bind(this), 1000);
    this.agencyManager = agencyManager;
    this.event = new FireableUpdateEventListener<AgenciesHomeModel>();
  }

  get title(): string {
    return i18n.t('agencies.home.title');
  }

  get placeholder(): string {
    return 'agencies.home.searchPlaceholder';
  }

  get state(): AgenciesHomeState {
    return {
      error: this.error,
      loading: this.loading,
      agencies: this.agencies,
      pagination: this.pagination,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      searchString: this.searchString,
      statusFilter: this.statusFilter
    };
  }

  get listModel() {
    return new DefaultAgencyListModel(
      this.state.agencies,
      this.state.pagination,
      this.onAgencyListChange
    );
  }

  // 保證送給後端的 sort 永遠有效：sortField/sortOrder 若被清成 undefined
  // （例如 table re-render 重新發出空的 sort 事件），退回預設 id,asc，
  // 避免組出 sort=undefined,undefined 讓後端 500。
  protected get effectiveSort(): { sort: string; direction: 'asc' | 'desc' } {
    return {
      sort: this.sortField || AgencyListColumns.AGENCY_ID,
      direction: this.sortOrder || 'asc'
    };
  }

  async fetch(page: number | undefined = 1): Promise<void> {
    this.notify(true);
    try {
      const { sort, direction } = this.effectiveSort;
      const pageable = {
        page,
        sizePerPage: 10,
        sort,
        direction
      };
      const result: AgencyWithPagination =
        await this.agencyManager.getAgenciesWithPagination(
          pageable,
          this.searchString,
          undefined,
          this.statusFilter
        );
      this.pagination = result.pagination;
      this.agencies = result.agencies;
      this.notify(false);
    } catch (error: any) {
      this.notify(false, error);
    }
  }

  changeStatusFilter = (status: AgencyStatusFilter) => {
    if (status === this.statusFilter) {
      return;
    }
    this.statusFilter = status;
    this.fetch(1);
  };

  // 經銷商狀態是三態單選（全部 / 啟用中 / 已封存）。FilterMenuTab 底層是
  // MultiSelectCheckbox（多選、無單選變體），因此在 apply 時強制單選：
  // selectedValues 恆為長度 1 → 畫面永遠只有一個 tag，避免多 tag 跑版。
  private get statusFilterLabels(): Record<AgencyStatusFilter, string> {
    return {
      [AgencyStatusFilter.ALL]: i18n.t('agencies.status.all'),
      [AgencyStatusFilter.ACTIVE]: i18n.t('agencies.status.active'),
      [AgencyStatusFilter.ARCHIVED]: i18n.t('agencies.status.archived')
    };
  }

  private get selectedStatusFilterLabel(): string {
    return this.statusFilterLabels[this.statusFilter];
  }

  private get statusFilterOptionLabels(): string[] {
    const labels = this.statusFilterLabels;
    return [
      labels[AgencyStatusFilter.ALL],
      labels[AgencyStatusFilter.ACTIVE],
      labels[AgencyStatusFilter.ARCHIVED]
    ];
  }

  private labelToStatusFilter(label: string): AgencyStatusFilter {
    const labels = this.statusFilterLabels;
    if (label === labels[AgencyStatusFilter.ACTIVE]) {
      return AgencyStatusFilter.ACTIVE;
    }
    if (label === labels[AgencyStatusFilter.ARCHIVED]) {
      return AgencyStatusFilter.ARCHIVED;
    }
    return AgencyStatusFilter.ALL;
  }

  handleOnStatusFilterApply = (values: string[]): void => {
    // 單選模式：MultiSelectCheckbox 已保證最多一個值；
    // 空陣列（點 tag 上的 × 移除）視為回「全部」(all)。
    const chosenLabel =
      values.length > 0
        ? values[values.length - 1]
        : this.statusFilterLabels[AgencyStatusFilter.ALL];
    this.changeStatusFilter(this.labelToStatusFilter(chosenLabel));
  };

  get filterMenuTabConfigs(): FilterMenuTabConfig[] {
    return [
      {
        filterType: i18n.t('agencies.filter.statusFilter'),
        menuTitle: i18n.t('agencies.filter.statusFilterMenuTitle'),
        tag: i18n.t('agencies.filter.statusTag'),
        selectedValues: [this.selectedStatusFilterLabel],
        options: this.statusFilterOptionLabels,
        applyMethod: this.handleOnStatusFilterApply,
        selectionMode: 'single'
      }
    ];
  }

  search(keyword: string) {
    this.searchString = keyword;
    if (this.searchString === '') {
      this.debouncedFetch && this.debouncedFetch.cancel();
      this.fetch();
    } else {
      this.debouncedFetch();
    }
  }

  onSort = async (field, direction) => {
    // 忽略無效（空）的排序事件，避免把 sortField/sortOrder 清成 undefined。
    // table 重新渲染時可能發出空 sort 事件，若照單全收會污染後續 fetch 的 sort。
    if (!field || !direction) {
      return;
    }
    if (field === this.sortField && direction === this.sortOrder) {
      return;
    }
    this.sortField = field;
    this.sortOrder = direction;
    await this.fetch(1);
  };

  onChangePage = async (page: number) => {
    if (page === this.pagination.page) {
      return;
    }
    this.pagination.page = page;
    await this.fetch(page);
  };

  notify(loading: boolean, error: Error | null = null) {
    this.error = error;
    this.loading = loading;
    this.event.fireEvent(this);
  }

  onAgencyListChange = async (type: string, props) => {
    if (type === 'sort') {
      await this.onSort(props.sortField, props.sortOrder);
      return;
    }
    await this.onChangePage(props.page);
  };
}

export class RestaurantsHomeModel extends DefaultAgenciesHomeModel {
  availableChannelOptions?: SelectOptions[];
  constructor(
    agencyManager: AgencyManager,
    private channelGroupManager: ChannelGroupManager = new DefaultChannelGroupManager()
  ) {
    super(agencyManager);
  }

  get title(): string {
    return i18n.t('restaurants.home.title');
  }

  get placeholder(): string {
    return i18n.t('restaurants.home.searchPlaceholder');
  }

  get listModel() {
    return new RestaurantListModel(
      this.state.agencies,
      this.state.pagination,
      this.onAgencyListChange
    );
  }

  async fetch(page: number | undefined = 1): Promise<void> {
    this.notify(true);
    try {
      if (!this.availableChannelOptions) {
        this.availableChannelOptions =
          await this.channelGroupManager.getAllowedSalesChannels();
      }

      const channelAddons = this.availableChannelOptions
        .map((option) => `channel.${option.value}`)
        .join(',');
      const { sort, direction } = this.effectiveSort;
      const pageable = {
        page,
        sizePerPage: 10,
        sort,
        direction
      };
      const result: AgencyWithPagination =
        await this.agencyManager.getAgenciesWithPagination(
          pageable,
          this.searchString,
          channelAddons,
          this.statusFilter
        );
      this.pagination = result.pagination;
      this.agencies = result.agencies;
      this.notify(false);
    } catch (error: any) {
      this.notify(false, error);
    }
  }
}
